import { yupResolver } from '@hookform/resolvers/yup';
import { InputLabel } from '@mui/material';
import { useForm } from 'react-hook-form';
import { Box, Button, Modal, Stack, styled, Text, Input } from '@/theme/index';
import { capitalizeString } from '@/utils/capitalizeString';
import { schemaServiceItemForm } from '@/utils/schemaForms/schemaForms';

export interface VehicleServiceCardUpdateModalProps {
  /** If true, the modal is modalOpen. */
  isModalOpen: boolean;
  /** Callback fired when the user tries to close the modal by either clicking on the backdrop or pressing the escape key */
  closeModal: () => void;
  /** If true, the modal is modalOpen. */
  serviceType: string;
  /** Callback to update Oram detail */
  handleSubmitForm: (value: string) => void;
}

const StyledModal = styled(Modal)`
  .MuiDialog-paper {
    width: 600px;
  }
`;

const StyledText = styled(Text)`
  cursor: pointer;
  text-decoration: underline;
  color: ${({ theme }) => theme.palette.primary.main};
`;

const StyledInput = styled(Input)`
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  input[type='number'] {
    -moz-appearance: textfield;
  }
`;

/**
 * This component is a generic modal used to display the claim vehicle form
 */
export const VehicleServiceCardUpdateModal: React.FC<
  VehicleServiceCardUpdateModalProps
> = ({ isModalOpen, closeModal, serviceType, handleSubmitForm }) => {
  const { handleSubmit, register, formState, getFieldState, getValues, reset } =
    useForm<{
      serviceItemValue: string;
    }>({
      mode: 'onTouched',
      resolver: yupResolver(schemaServiceItemForm),
    });

  const isRegistration = serviceType === 'registration';

  return (
    <StyledModal open={isModalOpen} onClose={closeModal}>
      <Stack direction="column" alignItems="center">
        {isRegistration ? (
          <Text variant="h3">
            Enter your vehicles registration expiration date
          </Text>
        ) : (
          <Text variant="h3">Enter the mileage of your last {serviceType}</Text>
        )}
        <Box
          component="form"
          sx={{ width: '100%' }}
          onSubmit={handleSubmit(() => {
            handleSubmitForm(getValues('serviceItemValue'));
            reset();
            closeModal();
          })}
        >
          <InputLabel
            htmlFor="serviceItemValue"
            sx={{ fontSize: '1rem', fontWeight: 700, color: 'black' }}
          >
            {isRegistration
              ? 'Expiration Date'
              : `${capitalizeString(serviceType)} mileage`}
          </InputLabel>
          <StyledInput
            {...register('serviceItemValue')}
            name="serviceItemValue"
            type={isRegistration ? 'date' : 'number'}
            variant="outlined"
            fullWidth
            error={getFieldState('serviceItemValue').invalid}
          />
          {formState.errors.serviceItemValue?.message}
          <Button
            variant="contained"
            size="small"
            fullWidth
            type="submit"
            sx={{ mt: 1.5 }}
            color="success"
          >
            Update
          </Button>
          <StyledText
            variant="body2"
            sx={{ mt: 2.5, textAlign: 'center' }}
            onClick={() => {
              reset();
              closeModal();
            }}
          >
            Go back
          </StyledText>
        </Box>
      </Stack>
    </StyledModal>
  );
};
